import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePersonalDataGMValidationSchema } from '../validation-gm';
import { Row, Col, Form, FormGroup, FormControl, Button } from 'react-bootstrap';
import { useAppAlertService, AppAlertUI } from 'App/components/utils/alerts/AppAlertService';
import { useWidgetService } from 'Services/widget';
import { useGarantieMaxActions, IProductDataGM } from '../GarantieMax';
import { SelectElement } from '../../base-ui-elements/SelectElement';
import { ILeadData } from 'Services/widgets/interfaces';
import { ICFFactorTypeOption } from '../../factor-service';
import GenderOptions from '../../genderOptions';
import { LoadingSpinner } from 'App/components/utils/LoadingSpinner';
import { DropdownDateField } from '../../base-ui-elements/DropdownDateField';

import leftArrowIconUrl from 'assets/bf-images/left-arrow.svg';
import { GMBFStep } from 'App/components/widgets/garantie-max/enum';

export interface IPersonalDataStepGM {
  firstName: string;
  lastName: string;
  email: string;
  optInNewsletter?: boolean;
  gender: string;
  street: string;
  houseNumber: string;
  zipCode: ICFFactorTypeOption | string;
  city: string;
  birthDate: string;
  phone: string;
}

const initialData: IPersonalDataStepGM = {
  gender: '',
  firstName: '',
  lastName: '',
  zipCode: '',
  city: '',
  street: '',
  houseNumber: '',
  phone: '',
  birthDate: '',
  email: '',
  optInNewsletter: false,
};

export interface IPersonalDataStepProps {
  lead: ILeadData;
  productData: IProductDataGM;
}

export const PersonalDataStep: React.FC<IPersonalDataStepProps> = ( { lead, productData } ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const actions = useGarantieMaxActions();
  const personalDataValidationSchema = usePersonalDataGMValidationSchema();
  const { showAlert } = useAppAlertService();
  const service = useWidgetService();

  const leadPersonalData = lead.personalData as IPersonalDataStepGM;

  let personalDataFields: IPersonalDataStepGM = leadPersonalData !== null ? leadPersonalData : initialData;

  const zipCodeList = React.useMemo<ICFFactorTypeOption[]>( () => {
    if ( productData !== null ) {
      const zipCodeFactor = productData.factors.find( ( f ) => f.label === 'Postleitzahl' );
      if ( zipCodeFactor !== undefined ) {
        return [ ...zipCodeFactor.values ].sort( ( a, b ) => {
          const aKey = parseInt( a.key as string );
          const bKey = parseInt( b.key as string );
          return aKey - bKey;
        } );
      }
    }
    return [];
  }, [ productData ] );

  const formContext = useForm<IPersonalDataStepGM>( {
    mode: 'onChange',
    resolver: yupResolver( personalDataValidationSchema ),
  } );
  const { errors, control, formState } = formContext;
  const formatDropdownDate: string = 'YYYY-MM-DD';
  const endDateToDropdownDate = dayjs().subtract( 18, 'years' ).format( formatDropdownDate );

  const onSubmit = React.useCallback( async ( formData: IPersonalDataStepGM ) => {
    const result = await service.savePersonalData( formData );
    if ( result.errorCode === 0 ) {
      actions.goToStep( 'payment', true );
    } else {
      showAlert( {
        message: t( 'base:forms.messages.errorSave' ),
        type: 'danger',
      } );
    }
  }, [ actions, service, showAlert, t ] );

  if ( lead.subStep2TariffData === null ) {
    actions.goToSubStep( 'subStep2', false );
  }

  const defaultValueDate = ( date: string ): string => {
    if ( date ) {
      const newDate = date.split( '.' ).reverse().join( '-' );
      return dayjs( newDate ).format( formatDropdownDate );
    }
    return '';
  };

  if ( productData === null ) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider { ...formContext }>
      <Row className="mt-3" id="personal-data-step">
        <Row className="max-w-450">
          <Col md={ 12 } id="alert-personal-page">
            <AppAlertUI />
          </Col>
          <Col md={ 12 } className="sticky-top">
            <button
              id={ `${GMBFStep.PersonalDataStep}_back` }
              type="button"
              className="btn btn-link m-0 p-0 text-c-black f-16 back-button"
              onClick={ () => actions.goToSubStep( 'subStep2', false ) }
            >
              <img src={ leftArrowIconUrl } alt="Back" />
              <span className="back-text pl-3 align-middle">
                { t( 'bookingFunnel.previousBtn' ) }
              </span>
            </button>
          </Col>
          <Col md={ 12 } className="head-tab text-center px-0">
            <h1 className="f-26 text-c-black">{ t( 'bookingFunnel.personalData.stepTitle' ) }</h1>
          </Col>
          <Col md={ 12 } className="head-tab mt-4 mb-5 px-0 text-center">
            <h2 className="f-18 text-c-black">{ t( 'bookingFunnel.personalData.stepSubTitleGM' ) }</h2>
          </Col>
          <Col md={ 12 } className="bf-custom-px">
            <Form noValidate onSubmit={ formContext.handleSubmit( onSubmit ) }>
              <Row>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="email"
                    control={ control }
                    defaultValue={ personalDataFields.email }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.email' ) }
                        </Form.Label>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.email' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { errors[props.name]?.message }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="optInNewsletter"
                    control={ control }
                    defaultValue={ personalDataFields.optInNewsletter }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name } className="gm-checkbox-box">
                        <FormControl
                          { ...props }
                          className="form-check-input m-0 wd-24"
                          type="checkbox"
                          isInvalid={ errors?.[props.name] !== undefined }
                          onChange={ ( e: React.ChangeEvent<HTMLInputElement> ) =>
                            props.onChange( e.target.checked )
                          }
                          checked={ props.value }
                          onBlur={ props.onBlur }
                        />
                        <Form.Label className="f-16 checkbox-label">
                          { t( 'bookingFunnel.personalData.optInNewsletter' ) }
                        </Form.Label>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Form.Label>{ t( 'bookingFunnel.personalData.title' ) }</Form.Label>
                  <Controller
                    name="gender"
                    control={ control }
                    defaultValue={ personalDataFields.gender }
                    render={ ( props ) => (
                      <FormGroup className="custom-btn-radio mb-2">
                        { GenderOptions.map( ( o, idx ) => {
                          return (
                            <Fragment key={ idx }>
                              <FormControl
                                { ...props }
                                className="form-check-input"
                                type="radio"
                                id={ o.value }
                                value={ o.value }
                                checked={ props.value === o.value }
                                onBlur={ props.onBlur }
                              />
                              <Form.Label
                                className="btn btn-border-radio"
                                htmlFor={ o.value }
                              >
                                { t( `bookingFunnel.personalData.${o.value}` ) }
                              </Form.Label>
                            </Fragment>
                          );
                        } ) }
                        <Form.Control
                          type="hidden"
                          isInvalid={ errors.gender !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'base:forms.messages.fieldRequired',
                            { fieldLabel: t( 'bookingFunnel.personalData.title' ) } ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="firstName"
                    control={ control }
                    defaultValue={ personalDataFields.firstName }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.firstName' ) }
                        </Form.Label>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.firstName' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'base:forms.messages.fieldRequired',
                            { fieldLabel: t( 'bookingFunnel.personalData.firstName' ) } ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="lastName"
                    control={ control }
                    defaultValue={ personalDataFields.lastName }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.lastName' ) }
                        </Form.Label>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.lastName' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'base:forms.messages.fieldRequired',
                            { fieldLabel: t( 'bookingFunnel.personalData.lastName' ) } ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <DropdownDateField
                    nameField="birthDate"
                    labelField={ t( 'bookingFunnel.personalData.birthDate' ) }
                    valueField={ defaultValueDate( personalDataFields.birthDate ) }
                    endDate={ endDateToDropdownDate }
                    isRequired={ true }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="street"
                    control={ control }
                    defaultValue={ personalDataFields.street }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.street' ) }
                        </Form.Label>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.street' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'base:forms.messages.fieldRequired',
                            { fieldLabel: t( 'bookingFunnel.personalData.street' ) } ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="houseNumber"
                    control={ control }
                    defaultValue={ personalDataFields.houseNumber }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.houseNumber' ) }
                        </Form.Label>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.houseNumber' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'base:forms.messages.fieldRequired',
                            { fieldLabel: t( 'bookingFunnel.personalData.houseNumber' ) } ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col md={ 12 } className="mb-4">
                  <Controller
                    name="city"
                    control={ control }
                    defaultValue={ personalDataFields.city }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.city' ) }
                        </Form.Label>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.city' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'base:forms.messages.fieldRequired',
                            { fieldLabel: t( 'bookingFunnel.personalData.city' ) } ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
                <Col sm={ 12 } className="mb-4">
                  { zipCodeList.length > 0 ?
                    <Controller
                      name="zipCode"
                      control={ control }
                      defaultValue={
                        personalDataFields.zipCode ? personalDataFields.zipCode :
                        lead.subStep1TariffData?.zipcode ? lead.subStep1TariffData?.zipcode : ''
                      }
                      render={ ( props ) => (
                        <FormGroup controlId={ props.name }>
                          <Form.Label>
                            { t( 'bookingFunnel.personalData.postCode' ) }
                          </Form.Label>
                          <SelectElement
                            labelField={ t( 'bookingFunnel.personalData.postCode' ) }
                            nameField={ props.name }
                            selectValue={ props.value }
                            listValues={ zipCodeList }
                          />
                          <Form.Control
                            type="hidden"
                            isInvalid={ errors[props.name] !== undefined }
                          />
                          <Form.Control.Feedback type="invalid">
                            { t( 'base:forms.messages.fieldRequired',
                              { fieldLabel: t( 'bookingFunnel.personalData.postCode' ) } ) }
                          </Form.Control.Feedback>
                        </FormGroup>
                      ) }
                    />
                    :
                    <Controller
                      name="zipCode"
                      control={ control }
                      defaultValue={ personalDataFields.zipCode }
                      render={ ( props ) => (
                        <FormGroup controlId={ props.name }>
                          <Form.Label>
                            { t( 'bookingFunnel.personalData.postCode' ) }
                          </Form.Label>
                          <Form.Control
                            { ...props }
                            type="text"
                            placeholder={ t( 'bookingFunnel.personalData.postCode' ) }
                            isInvalid={ errors[props.name] !== undefined }
                          />
                          <Form.Control.Feedback type="invalid">
                            { t( 'base:forms.messages.fieldRequired',
                              { fieldLabel: t( 'bookingFunnel.personalData.postCode' ) } ) }
                          </Form.Control.Feedback>
                        </FormGroup>
                      ) }
                    />
                  }
                </Col>
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="phone"
                    control={ control }
                    defaultValue={ personalDataFields.phone }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>
                          { t( 'bookingFunnel.personalData.phone' ) }
                        </Form.Label>
                        <span className="gm-info f-12 mt-1">{ t( 'bookingFunnel.personalData.phoneFormat' ) }</span>
                        <Form.Control
                          { ...props }
                          type="text"
                          placeholder={ t( 'bookingFunnel.personalData.phone' ) }
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { errors[props.name]?.message }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
              </Row>
              <Row className="m-0 mt-5">
                <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0">
                  <Button
                    id={ `${GMBFStep.PersonalDataStep}_next` }
                    variant="primary"
                    disabled={ formState.isSubmitting }
                    className="mr-0"
                    type="submit"
                  >
                    { t( 'bookingFunnel.nextBtn' ) }
                  </Button>
                </Col>
                <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0 mt-2">
                  <Button
                    id={ `${GMBFStep.PersonalDataStep}_back` }
                    type="button"
                    variant="link"
                    className="mt-2 mb-2 mx-0 py-0 text-c-black custom-back-button"
                    onClick={ () => actions.goToSubStep( 'subStep2', false ) }
                  >
                    { t( 'bookingFunnel.previousBtn' ) }
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Row>
    </FormProvider>
  );
};
